import { useCallback, useMemo, useState } from 'react';

export const MAX_SIZE_MB = 2;
export const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
export const MIN_DIMENSION_PX = 512;

export type UploadLogoStep = 'select' | 'checking' | 'uploaded' | 'error';

export type RequirementCheck = {
  label: string;
  passed: boolean;
};

// One state object instead of parallel useState calls: every step transition
// is a single atomic update (one render), and the fields can never disagree
// (e.g. step 'uploaded' with a stale errorMessage).
type UploadLogoState = {
  step: UploadLogoStep;
  file: File | null;
  fileDataUrl: string | null;
  checks: RequirementCheck[];
  errorMessage: string | null;
};

const INITIAL_STATE: UploadLogoState = {
  step: 'select',
  file: null,
  fileDataUrl: null,
  checks: [],
  errorMessage: null,
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** True when the file's bytes start with the PNG signature. */
const isPngFile = async (file: File): Promise<boolean> => {
  const header = new Uint8Array(
    await file.slice(0, PNG_SIGNATURE.length).arrayBuffer(),
  );
  return PNG_SIGNATURE.every((byte, index) => header[index] === byte);
};

/** True when the file parses as an XML document rooted on `<svg>`. */
const isSvgFile = async (file: File): Promise<boolean> => {
  try {
    const doc = new DOMParser().parseFromString(
      await file.text(),
      'image/svg+xml',
    );
    return (
      doc.documentElement.localName === 'svg' &&
      doc.getElementsByTagName('parsererror').length === 0
    );
  } catch {
    return false;
  }
};

/** Natural pixel size of an image data URL (SVGs resolve as scalable → ok). */
const imageMeetsMinDimensions = (dataUrl: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(
        img.naturalWidth >= MIN_DIMENSION_PX &&
          img.naturalHeight >= MIN_DIMENSION_PX,
      );
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });

export type UseUploadBuyerLogoModalOptions = {
  /** Variant reminder shown as the first (never-failing) requirement. */
  versionLabel: string;
};

export type UseUploadBuyerLogoModalReturn = UploadLogoState & {
  requirementLabels: string[];
  handleFile: (candidate: File | null) => Promise<void>;
  reset: () => void;
};

/**
 * Owns the upload state machine: validates a candidate file client-side
 * (format, weight, dimensions) and moves through select → checking →
 * uploaded/error.
 */
export const useUploadBuyerLogoModal = ({
  versionLabel,
}: UseUploadBuyerLogoModalOptions): UseUploadBuyerLogoModalReturn => {
  const [state, setState] = useState<UploadLogoState>(INITIAL_STATE);

  const requirementLabels = useMemo(
    () => [
      versionLabel,
      `${MIN_DIMENSION_PX} × ${MIN_DIMENSION_PX} px minimum`,
      'PNG or SVG format',
      `${MAX_SIZE_MB} MB maximum`,
    ],
    [versionLabel],
  );

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  const handleFile = useCallback(
    async (candidate: File | null) => {
      if (!candidate) return;
      setState({ ...INITIAL_STATE, step: 'checking' });
      try {
        const dataUrl = await fileToDataUrl(candidate);

        const sizeOk = candidate.size <= MAX_SIZE_BYTES;
        // The declared MIME type / extension only picks which content check
        // runs; the bytes have the final word (a text file renamed .png or a
        // broken SVG both fail).
        const extension = candidate.name.split('.').pop()?.toLowerCase();
        const looksSvg =
          candidate.type === 'image/svg+xml' || extension === 'svg';
        const looksPng = candidate.type === 'image/png' || extension === 'png';
        const isSvg = looksSvg && (await isSvgFile(candidate));
        const isPng = !isSvg && looksPng && (await isPngFile(candidate));
        const formatOk = isSvg || isPng;
        // SVGs are resolution-independent; only raster files get measured. A
        // file that already failed the format check isn't double-flagged here.
        const dimensionsOk = formatOk
          ? isSvg || (await imageMeetsMinDimensions(dataUrl))
          : true;

        const checks: RequirementCheck[] = [
          // The variant (color/greyscale, light/dark) can't be verified
          // programmatically — it's a reminder, never flagged as failing.
          { label: requirementLabels[0], passed: true },
          { label: requirementLabels[1], passed: dimensionsOk },
          { label: requirementLabels[2], passed: formatOk },
          { label: requirementLabels[3], passed: sizeOk },
        ];

        let errorMessage: string | null = null;
        if (!formatOk) {
          // Right type but failing content check → the file is damaged, not
          // the wrong format.
          errorMessage =
            looksSvg || looksPng
              ? "We couldn't read that file — it looks damaged. Re-export it, then try again."
              : "That file type isn't supported. Use PNG or SVG, then try again.";
        } else if (!sizeOk) {
          errorMessage = `That file's too large. Keep it under ${MAX_SIZE_MB} MB, then try again.`;
        } else if (!dimensionsOk) {
          errorMessage = `That image is too small. Use at least ${MIN_DIMENSION_PX} × ${MIN_DIMENSION_PX} px, then try again.`;
        }

        setState(
          errorMessage
            ? { ...INITIAL_STATE, step: 'error', checks, errorMessage }
            : {
                step: 'uploaded',
                file: candidate,
                fileDataUrl: dataUrl,
                checks,
                errorMessage: null,
              },
        );
      } catch {
        setState({
          ...INITIAL_STATE,
          step: 'error',
          errorMessage: "We couldn't read that file. Please try again.",
        });
      }
    },
    [requirementLabels],
  );

  return { ...state, requirementLabels, handleFile, reset };
};
